import { tracked } from '@glimmer/tracking';

import type { APIData, GeneralComponentClass } from 'webapp-types';

import CustomQuerySetupPrebuiltFormCloneableRowComponent from 'webapp/components/custom-query/setup/prebuilt/cloneable-row';

import type CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import CustomMetricSetupFormItemGroupBase from 'webapp/models-custom/custom-metric-setup-form/item/group/base';
import type CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';

export default class CustomMetricSetupFormItemGroupCloneable extends CustomMetricSetupFormItemGroupBase {
	cloneSettings: APIData.CustomQuery.Prebuilt.BuilderItem['cloneable'];

	@tracked shownItems: CustomMetricSetupFormItemField[][] = []; // array of field arrays
	@tracked operator = 'or';

	override isCloneable = true;

	constructor(
		groupData: APIData.CustomQuery.Prebuilt.BuilderItem,
		form: CustomMetricSetupFormPrebuilt,
	) {
		super(groupData, form);
		// cloneSettings are always defined for cloneable group
		this.cloneSettings = groupData.cloneable;
	}

	get allFieldsInGroup(): CustomMetricSetupFormItemField[] {
		const fields: CustomMetricSetupFormItemField[] = [];
		this.shownItems.forEach((cloneableGroup) => {
			cloneableGroup.forEach((item) => {
				fields.push(item);
			});
		});
		return fields;
	}

	get isDefault(): boolean {
		const items: CustomMetricSetupFormItemField[] = [];
		this.shownItems.forEach((clonedGroupItems) => {
			items.push(...clonedGroupItems);
		});
		return items.every((item) => {
			return item.isDefault;
		});
	}

	get isOperatorPreset(): boolean {
		return !!this.cloneSettings?.operator;
	}

	get shownItemsComponent(): GeneralComponentClass {
		return CustomQuerySetupPrebuiltFormCloneableRowComponent;
	}

	get possibleOperators(): LabelValuePairString[] {
		let values = [
			{ value: 'and', label: 'AND' },
			{ value: 'or', label: 'OR' },
		];
		if (this.isOperatorPreset) {
			values = values.filter((item) => item.value === this.operator);
		}
		return values;
	}

	get max(): number {
		return this.cloneSettings?.max || 99;
	}

	get valueFormatted(): APIData.CustomQuery.Prebuilt.CloneableGroupValue {
		const items: APIData.CustomQuery.Prebuilt.CloneableGroupValue['items'] = [];
		// array of group values
		this.shownItems.forEach((cloneableGroup) => {
			const groupValue: APIData.CustomQuery.Prebuilt.CloneableGroupValue['items'][number] =
				{};
			cloneableGroup.forEach((item) => {
				if (!item.isHidden) {
					groupValue[item.id] = item.valueFormatted;
				}
			});
			items.push(groupValue);
		});

		return {
			operator: this.operator,
			items: items,
		};
	}

	// returns array / object that will be merged into settings sent to BE
	get valueRecord(): APIData.CustomQuery.Prebuilt.SettingsData {
		return {
			[this.id]: this.valueFormatted,
		};
	}

	setInitialValues(settings: APIData.CustomQuery.Prebuilt.SettingsData): void {
		const initialValue =
			(settings?.[this.id] as APIData.CustomQuery.Prebuilt.CloneableGroupValue | undefined) ||
			null;

		let initialCloneablesCount = 1;
		if (initialValue) {
			if (initialValue.operator) {
				this.operator = initialValue.operator;
			}
			if (initialValue.items?.length) {
				initialCloneablesCount = initialValue.items.length;
			}
		}
		this.shownItems = [];
		for (let i = 0; i < initialCloneablesCount; i++) {
			this.addShownItem();
		}
		if (this.cloneSettings?.operator) {
			this.operator = this.cloneSettings.operator;
		}

		if (initialValue?.items?.length) {
			this.shownItems.forEach((cloneable, key) => {
				const values = initialValue.items[key];
				if (values) {
					cloneable.forEach((item) => {
						const value = values[item.id];
						if (value) {
							item.setInitialSelected(value);
						}
					});
				}
			});
		}
	}

	addShownItem() {
		const items: CustomMetricSetupFormItemField[] = [];
		this.rawItems.forEach((itemData) => {
			const item = this.form.parseBuilderItem(itemData, {}) as CustomMetricSetupFormItemField;
			items.push(item);
		});
		this.shownItems = [...this.shownItems, items];
	}

	removeShownItem(index: number): void {
		const toRemove = this.shownItems[index];
		if (toRemove) {
			this.shownItems = this.shownItems.filter((item) => item !== toRemove);
		}
	}
}
